import React from 'react';
import Select, { components as selectOnderdelen, MultiValueProps, Props as SelectProps } from 'react-select';

export interface StyledSelectOption {
  value: string;
  label: string;
}

export interface StyledSelectGroupedOption {
  label: string;
  options: StyledSelectOption[];
}

interface StyledSelectProps extends Omit<SelectProps, 'styles'> {
  variant?: 'default' | 'compact';
  width?: string;
}

interface StyledMultiSelectProps extends Omit<SelectProps, 'styles' | 'isMulti'> {
  variant?: 'default' | 'compact';
  width?: string;
  /** Houdt het vak op één regel; de gekozen waarden wikkelen dan niet naar een tweede. */
  eenRegel?: boolean;
}

/**
 * Kleuren als var()-verwijzing en niet als hex, zodat de keuzelijst het thema volgt.
 *
 * Emotion - dat react-select onder water gebruikt - schrijft deze waarden letterlijk in
 * de stylesheet, dus de var() wordt door de browser opgelost en verandert mee zodra
 * data-thema omgaat. Er hoeft dus niets opnieuw te renderen. Ook het menuPortal, dat in
 * document.body hangt, erft de variabelen gewoon van :root.
 */
const kleur = (naam: string) => `rgb(var(${naam}))`;

export const themaSelectStijlen = (
  variant: 'default' | 'compact' = 'default',
  eenRegel = false,
) => {
  const minHeight = variant === 'compact' ? '32px' : '40px';

  return {
    control: (base: any, state: any) => ({
      ...base,
      minHeight,
      fontSize: '14px',
      backgroundColor: kleur('--vlak-kaart'),
      border: `1px solid ${kleur('--vlak-4')}`,
      borderRadius: '0.5rem',
      boxShadow: state.isFocused ? `0 0 0 1px ${kleur('--merk-inkt')}` : 'none',
      '&:hover': {
        borderColor: kleur('--merk-inkt'),
      },
    }),
    // Zonder menuPortalTarget hangt het menu gewoon in de pagina, en dan is de z-index van
    // react-select zelf - 1 - te laag voor alles wat op een kaart eronder een eigen laag
    // heeft: de lijnen van een oefening staan op z-10 en tekenden dwars door dit menu heen.
    // 50 is wat de andere uitklappers hier aanhouden, zoals SortMenu ernaast.
    menu: (base: any) => ({
      ...base,
      zIndex: 50,
      backgroundColor: kleur('--vlak-kaart'),
      border: `1px solid ${kleur('--vlak-3')}`,
    }),
    // Boven een venster uit. Met menuPortalTarget hangt het menu in document.body en dus
    // buiten de z-50 van een modal; zonder dit valt het eronder en lijkt het niet te openen.
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: '14px',
      backgroundColor: state.isSelected
        ? kleur('--merk-vlak')
        : state.isFocused
        ? kleur('--merk-tint')
        : kleur('--vlak-kaart'),
      // Wit en niet --omgekeerd-inkt: de geselecteerde regel staat op het merkblauw, en
      // dat blijft in beide standen een gekleurd vlak met witte tekst erop.
      color: state.isSelected ? '#ffffff' : kleur('--inkt-7'),
      '&:hover': {
        backgroundColor: state.isSelected ? kleur('--merk-vlak') : kleur('--merk-tint'),
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: kleur('--inkt-7'),
    }),
    input: (base: any) => ({
      ...base,
      color: kleur('--inkt-7'),
    }),
    // Met `eenRegel` blijft het vak even hoog als de velden ernaast: de gekozen waarden
    // wikkelen niet naar een tweede regel, maar wat niet past valt weg. Alleen zinvol bij
    // een lijst die zelf al samenvat wat er niet meer in beeld is - zie de filter op het
    // oefeningenoverzicht, die na de eerste waarde een "+2" toont.
    valueContainer: (base: any) => ({
      ...base,
      ...(eenRegel ? { flexWrap: 'nowrap', overflow: 'hidden' } : {}),
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: kleur('--merk-tint-sterk'),
      // Niet meekrimpen met de ruimte: een half afgekapt woord leest slechter dan een
      // waarde die je pas ziet als je het menu opent.
      ...(eenRegel ? { flexShrink: 0, maxWidth: '100%' } : {}),
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: kleur('--merk-inkt'),
      fontSize: '12px',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: kleur('--merk-inkt'),
      ':hover': {
        backgroundColor: kleur('--merk-inkt'),
        color: kleur('--vlak-kaart'),
      },
    }),
    groupHeading: (base: any) => ({
      ...base,
      backgroundColor: kleur('--vlak-2'),
      color: kleur('--inkt-7'),
      fontWeight: '600',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '8px 12px',
      margin: '0',
      borderBottom: `1px solid ${kleur('--vlak-3')}`,
    }),
    group: (base: any) => ({
      ...base,
      paddingTop: '0',
      paddingBottom: '8px',
    }),
  };
};

/**
 * react-select tekent de plaatshouder, het pijltje en de scheidingsstreep niet uit
 * `styles` maar uit zijn eigen hsl-thema. Zonder deze aanvulling blijft de plaatshouder
 * dus lichtgrijs in donkere modus, hoe compleet het stijlobject hierboven ook is.
 */
export const themaSelectThema = (basis: any) => ({
  ...basis,
  colors: {
    ...basis.colors,
    primary: 'rgb(var(--merk-vlak))',
    primary25: 'rgb(var(--merk-tint))',
    primary50: 'rgb(var(--merk-tint-sterk))',
    neutral0: 'rgb(var(--vlak-kaart))',
    neutral5: 'rgb(var(--vlak-2))',
    neutral10: 'rgb(var(--vlak-2))',
    neutral20: 'rgb(var(--vlak-4))',
    neutral30: 'rgb(var(--inkt-4))',
    neutral40: 'rgb(var(--inkt-5))',
    neutral50: 'rgb(var(--inkt-4))',
    neutral60: 'rgb(var(--inkt-5))',
    neutral80: 'rgb(var(--inkt-7))',
  },
});

const getSelectStyles = themaSelectStijlen;

export const StyledSelect: React.FC<StyledSelectProps> = ({
  variant = 'default',
  width,
  ...props
}) => {
  return (
    <div style={width ? { width, minWidth: width } : undefined}>
      <Select
        {...props}
        styles={getSelectStyles(variant)}
        theme={themaSelectThema}
        className="text-sm"
        classNamePrefix="react-select"
      />
    </div>
  );
};

export const StyledMultiSelect: React.FC<StyledMultiSelectProps> = ({
  variant = 'default',
  width,
  eenRegel = false,
  ...props
}) => {
  return (
    <div style={width ? { width, minWidth: width } : undefined}>
      <Select
        {...props}
        isMulti
        styles={getSelectStyles(variant, eenRegel)}
        theme={themaSelectThema}
        className="text-sm"
        classNamePrefix="react-select"
      />
    </div>
  );
};

/**
 * Eén gekozen filter in beeld, de rest als "+2" erachter.
 *
 * Hoort bij `eenRegel`. Het vak is even breed als het zoekveld ernaast, en daar passen twee waarden niet in. Ze
 * onder elkaar zetten - wat react-select uit zichzelf doet - maakt het vak twee regels hoog
 * en duwt de hele rij scheef; ze samen laten krimpen levert drie half afgekapte woorden op.
 * Met een teller blijft de rij staan, en welke filters aan staan zie je in het menu zelf:
 * die regels staan daar aangevinkt. Gebruikt op Oefeningen en in Team beheer.
 */
export const FilterChip = (props: MultiValueProps<unknown, boolean>) => {
  const gekozenFilters = (props.selectProps.value ?? []) as readonly StyledSelectOption[];
  const plek = gekozenFilters.findIndex(optie => optie.value === (props.data as StyledSelectOption).value);

  if (plek === 0) return <selectOnderdelen.MultiValue {...props} />;
  if (plek > 1) return null;

  return (
    <span className="ml-1 self-center whitespace-nowrap text-xs text-gray-500">
      +{gekozenFilters.length - 1}
    </span>
  );
};
